import {
  buildPublicState,
  getLockedDropTargetSprintId,
  getRawState,
} from './internalState';
import { clearSidebarRowHighlights, syncDragVisualClasses, syncSidebarRowHighlights } from './visualSync';

const listeners = new Set();
/** High-frequency subscribers (drag ghost) — called every pointer event with raw coords. */
const positionListeners = new Set();

/** Snapshot of the values React subscribers actually render from. */
const getMeaningfulSnapshot = () => {
  const state = getRawState();
  return {
    active: state.active,
    hoverTargetSprintId: state.hoverTargetSprintId,
    lockedTarget: getLockedDropTargetSprintId(),
    isOverSidebar: state.isOverSidebar,
    taskId: state.taskId,
  };
};

let lastNotifiedSnapshot = null;

const snapshotsEqual = (a, b) =>
  !!a && !!b
  && a.active === b.active
  && a.hoverTargetSprintId === b.hoverTargetSprintId
  && a.lockedTarget === b.lockedTarget
  && a.isOverSidebar === b.isOverSidebar
  && a.taskId === b.taskId;

export function clearLastNotifiedSnapshot() {
  lastNotifiedSnapshot = null;
}

export function getState() {
  return buildPublicState();
}

/**
 * PERFORMANCE: notify() runs on every pointer event during a drag.
 * Ghost position updates every event via direct DOM, so React subscribers
 * only hear about meaningful state changes — not raw movement.
 */
export function notify() {
  syncDragVisualClasses();
  syncSidebarRowHighlights();

  const snapshot = getMeaningfulSnapshot();
  if (snapshotsEqual(snapshot, lastNotifiedSnapshot)) {
    positionListeners.forEach((fn) => {
      try {
        fn(getRawState().lastPointer, getState());
      } catch (err) {
        console.warn('[MissionToSidebarBridge] position listener error:', err);
      }
    });
    return;
  }
  lastNotifiedSnapshot = snapshot;

  listeners.forEach((fn) => {
    try {
      fn(getState());
    } catch (err) {
      console.warn('[MissionToSidebarBridge] listener error:', err);
    }
  });
  positionListeners.forEach((fn) => {
    try {
      fn(getRawState().lastPointer, getState());
    } catch (err) {
      console.warn('[MissionToSidebarBridge] position listener error:', err);
    }
  });
}

export function subscribeMissionToSidebar(callback) {
  listeners.add(callback);
  callback(getState());
  return () => listeners.delete(callback);
}

/**
 * High-frequency subscription for cursor-following visuals (drag ghost).
 * Fires on EVERY pointer event with (lastPointer, state) — do direct DOM work
 * in the callback, never React state updates.
 */
export function subscribeMissionToSidebarPointer(callback) {
  positionListeners.add(callback);
  return () => positionListeners.delete(callback);
}
